import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// this module calls dayjs.utc(), so it registers the plugin itself rather
// than relying on whichever entry point happened to load first
dayjs.extend(utc);

export type TimeRange =
  | "today"
  | "last-week"
  | "last-month"
  | "last-year"
  | "this-week"
  | "this-month"
  | "this-year"
  | "all-time";

export const CHAT_TIME_RANGE = [
  "today",
  "last-week",
  "last-month",
  "last-year",
  "all-time",
] as const;

export interface DateRange {
  startDate: string;
  endDate: string;
}

export const getDateForTimeFrame = (
  timeFrame: TimeRange | null,
): string | undefined => {
  if (!timeFrame) return undefined;
  const now = dayjs.utc().startOf("day");

  switch (timeFrame) {
    case "today":
      return now.toISOString().split("T")[0];
    case "this-week":
    case "last-week":
      return now.subtract(7, "day").toISOString().split("T")[0];
    case "this-month":
    case "last-month":
      return now.subtract(30, "day").toISOString().split("T")[0];
    case "this-year":
    case "last-year":
      return now.subtract(365, "day").toISOString().split("T")[0];
    case "all-time":
      return undefined;
    default:
      return undefined;
  }
};

export const filterDatesByTimeRange = (
  availableDates: string[],
  timeRange: TimeRange | null,
): string[] | undefined => {
  if (!timeRange) return undefined;

  const cutoffDate = getDateForTimeFrame(timeRange);

  // If all-time, return all dates
  if (!cutoffDate) {
    return availableDates.length > 0 ? availableDates : undefined;
  }

  // Filter to only include dates on or after the cutoff date
  const filteredDates = availableDates.filter((date) => date >= cutoffDate);

  return filteredDates.length > 0 ? filteredDates : undefined;
};

export const getTimestampsForTimeRange = (timeRange: TimeRange): DateRange => {
  const now = dayjs.utc();

  switch (timeRange) {
    case "today":
      return {
        startDate: now.startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "last-week":
    case "this-week":
      return {
        startDate: now.subtract(7, "day").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "last-month":
      return {
        startDate: now.subtract(1, "month").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "last-year":
      return {
        startDate: now.subtract(1, "year").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "this-month":
      return {
        startDate: now.subtract(30, "day").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "this-year":
      return {
        startDate: now.subtract(365, "day").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
    case "all-time":
    default:
      return {
        startDate: now.subtract(100, "year").startOf("day").toISOString(),
        endDate: now.endOf("day").toISOString(),
      };
  }
};
